import { kpRequest, kpParams, kpEndpoint } from '@/axios'
import { ZipStreamWriter } from '@/utils/zipStream'
import { DOWNLOAD_TYPES, DOWNLOAD_FILE_STATUS, FILE_FORMATS } from '@/constants'
import { DOWNLOAD_DELIVERABLE, DOWNLOAD_ASSET_SECTIONS } from '@/common-utils'

// KP-3919: the big-bundle download tier. The server hands over direct links
// for the selection (auth-checked, urls re-resolved server-side) and the
// BROWSER builds the zip, streaming every file straight from S3 onto the
// user's disk — no server bytes, no stored artifact, any total size.
//
// Two save strategies:
//  - picker: showSaveFilePicker (top-level Chrome/Edge) — user picks the spot.
//  - service worker: cross-origin iframes (the PLG shell) may not show a
//    picker, so the zip stream is handed to /public/kp-zip-sw.js and pulled
//    out as a regular browser download into the Downloads folder.

export class ClientZipCancelled extends Error {
  constructor() {
    super('Save cancelled')
    this.name = 'ClientZipCancelled'
  }
}

// The order form could not be produced. Marked rather than subclassed (one
// class per file), so the picker can say which half went wrong instead of
// blaming the archive.
const orderFormUnavailable = message => Object.assign(
  new Error(message), { isOrderFormFailure: true },
)
export const isOrderFormFailure = err => Boolean(err && err.isOrderFormFailure)

// Chrome refuses save pickers in cross-origin iframes; accessing
// top.document throws cross-origin, which is exactly the signal.
const pickerAllowedInFrame = () => {
  try {
    return Boolean(window.top.document)
  } catch (err) {
    return false
  }
}

const supportsPicker = () => typeof window !== 'undefined'
  && typeof window.showSaveFilePicker === 'function'
  && pickerAllowedInFrame()

// API presence is not the same as transferability: some engines expose
// ReadableStream and MessageChannel but cannot postMessage a stream, and the
// failure is a synchronous DataCloneError at hand-off — too late, because the
// picker path is unavailable in the frame this strategy exists for. Probe a
// throwaway transfer once and cache the answer.
let swTransferable = null
const canTransferStream = () => {
  if (swTransferable !== null) return swTransferable
  try {
    const channel = new MessageChannel()
    const probe = new ReadableStream()
    channel.port1.postMessage(probe, [probe])
    channel.port1.close()
    channel.port2.close()
    swTransferable = true
  } catch (err) {
    swTransferable = false
  }
  return swTransferable
}

const supportsSwDownload = () => typeof navigator !== 'undefined'
  && Boolean(navigator.serviceWorker)
  && typeof ReadableStream === 'function'
  && typeof MessageChannel === 'function'
  && canTransferStream()

export const supportsClientZip = () => supportsPicker() || supportsSwDownload()

const uniqueEntryName = used => (folder, rawName) => {
  const name = (rawName || 'download').replace(/[/\\]/g, '-').trim()
  let candidate = `${folder}/${name}`
  for (let copy = 2; used.has(candidate); copy += 1) {
    const dot = name.lastIndexOf('.')
    candidate = dot > 0
      ? `${folder}/${name.slice(0, dot)} (${copy})${name.slice(dot)}`
      : `${folder}/${name} (${copy})`
  }
  used.add(candidate)
  return candidate
}

// Must match BUNDLE_FOLDERS.COLLECTION in the backend's brandAssets.ts, so
// both download tiers build the same zip.
const COLLECTION_FOLDER = 'Collection'
const ORDER_FORM_POLL_MS = 1500
const ORDER_FORM_MAX_WAIT_MS = 2 * 60 * 1000

const sleep = ms => new Promise(resolve => { setTimeout(resolve, ms) })

// The order form is not a stored file — catalog-service generates it on
// demand and reports back on the download row. Server-side bundling picks it
// up at completion time; the client-zip tier has no equivalent moment, so it
// asks for one here and waits. Without this the archive silently omits the
// single most important file in it (KP-3919).
//
// The request is a selection of just the order form, which means the row is
// created with no bundle assets and the backend delivers the plain xlsx
// rather than zipping it server-side.
// Ask the server to generate one. Returns the download row's id, which is
// the handle the poll below waits on.
const startOrderFormExport = async ({ collectionId, retailerId }) => {
  const created = await kpRequest({
    ...kpEndpoint.downloads.collection,
    payload: {
      downloadType: DOWNLOAD_TYPES.COLLECTION_EXPORT.VALUE,
      respectiveId: collectionId,
      fileType: FILE_FORMATS.XLSX,
      deliverable: DOWNLOAD_DELIVERABLE.SELECTION,
      selectedAssets: [{ section: DOWNLOAD_ASSET_SECTIONS.ORDER_FORM }],
      ...(retailerId ? { retailerId } : {}),
    },
  })
  const downloadId = created.data.data && created.data.data.downloadId
  if (!downloadId) throw orderFormUnavailable('Could not start the collection export')
  return downloadId
}

// One poll: the finished asset, or null to keep waiting. Throws if the export
// failed outright, so the caller's loop only has to handle "not yet".
const pollOrderForm = async downloadId => {
  const res = await kpRequest({
    ...kpParams(kpEndpoint.downloads.collectionExportStatus, downloadId),
  })
  const { status, url, name } = res.data.data
  if (url) return { url, name: `${name}.xlsx`, folder: COLLECTION_FOLDER }
  if (status === DOWNLOAD_FILE_STATUS.ERROR.VALUE) {
    throw orderFormUnavailable('The collection export failed')
  }
  return null
}

const prepareOrderForm = async ({ collectionId, retailerId }) => {
  const downloadId = await startOrderFormExport({ collectionId, retailerId })
  const deadline = Date.now() + ORDER_FORM_MAX_WAIT_MS
  for (;;) {
    // eslint-disable-next-line no-await-in-loop
    const asset = await pollOrderForm(downloadId)
    if (asset) return asset
    if (Date.now() > deadline) {
      throw orderFormUnavailable('The collection export is taking too long to generate')
    }
    // eslint-disable-next-line no-await-in-loop
    await sleep(ORDER_FORM_POLL_MS)
  }
}

const wantsOrderForm = (deliverable, selectedAssets) => {
  if (deliverable !== DOWNLOAD_DELIVERABLE.SELECTION) return true
  return (selectedAssets || []).some(
    ref => ref.section === DOWNLOAD_ASSET_SECTIONS.ORDER_FORM,
  )
}

const resolveAssets = async ({ collectionId, deliverable, selectedAssets }) => {
  const res = await kpRequest({
    ...kpEndpoint.downloads.collectionAssetLinks,
    payload: {
      respectiveId: collectionId,
      deliverable,
      ...(selectedAssets ? { selectedAssets } : {}),
    },
  })
  return res.data.data.assets
}

// ---------------------------------------------------------------------------
// Strategy 1: File System Access picker (top-level frames).

// Pump one asset's bytes into the archive, carrying the running total in and
// out so nothing mutable has to be captured. `onBytes` lets the caller drain
// queued disk writes between chunks, which is what preserves back pressure.
const streamAssetIntoZip = async (zip, asset, { alreadyLoaded, onBytes }) => {
  const response = await fetch(asset.url)
  if (!response.ok) {
    throw new Error(`Could not fetch ${asset.name} (${response.status})`)
  }
  const reader = response.body.getReader()
  let loaded = alreadyLoaded
  for (;;) {
    // eslint-disable-next-line no-await-in-loop
    const { done, value } = await reader.read()
    if (done) return loaded
    zip.pushData(value)
    loaded += value.length
    // eslint-disable-next-line no-await-in-loop
    await onBytes(loaded)
  }
}

// Built outside the loop so the callback closes over its arguments only.
const chunkReporter = ({
  onProgress, afterChunk, assets, index,
}) => async loaded => {
  if (onProgress) {
    onProgress({
      loaded, fileIndex: index, fileCount: assets.length, currentFile: assets[index].name,
    })
  }
  await afterChunk()
}

// Walk the selection into a finished archive; returns the byte count.
const writeAssetsToZip = async ({
  zip, assets, onProgress, afterChunk,
}) => {
  const entryName = uniqueEntryName(new Set())
  let loaded = 0
  for (const [index, asset] of assets.entries()) {
    zip.startEntry(entryName(asset.folder, asset.name))
    // eslint-disable-next-line no-await-in-loop
    loaded = await streamAssetIntoZip(zip, asset, {
      alreadyLoaded: loaded,
      onBytes: chunkReporter({
        onProgress, afterChunk, assets, index,
      }),
    })
    zip.endEntry()
  }
  zip.finish()
  return loaded
}

const saveToHandle = async ({ handle, assets, onProgress }) => {
  const writable = await handle.createWritable()
  // The zip writer's sink is synchronous; disk writes are chained so back
  // pressure never loses ordering.
  let chain = Promise.resolve()
  const zip = new ZipStreamWriter(chunk => {
    chain = chain.then(() => writable.write(chunk))
  })
  try {
    const bytes = await writeAssetsToZip({
      zip, assets, onProgress, afterChunk: () => chain,
    })
    await chain
    await writable.close()
    return { files: assets.length, bytes }
  } catch (err) {
    await writable.abort().catch(() => {})
    throw err
  }
}

// ---------------------------------------------------------------------------
// Strategy 2: service-worker download (iframes / no picker).

// Pull-driven zip producer: the browser's own download consumption sets the
// pace (backpressure), each S3 file is fetched just in time.
// One step of the producer has two unrelated jobs — open the next entry, or
// pump a chunk of the one already open. Interleaving them in a single
// function was the complexity; they are split and hoisted out of the closure
// so each reads on its own. `ctx` carries the mutable cursor.

// Open the next asset's zip entry and its network reader. When the list is
// exhausted it closes the archive and settles the caller's promise instead.
const openNextEntry = async ctx => {
  const { assets, zip } = ctx
  if (ctx.index >= assets.length) {
    zip.finish()
    ctx.closed = true
    ctx.settle.resolve({ files: assets.length, bytes: ctx.loaded })
    return
  }
  const asset = assets[ctx.index]
  zip.startEntry(ctx.entryName(asset.folder, asset.name))
  const response = await fetch(asset.url)
  if (!response.ok) {
    throw new Error(`Could not fetch ${asset.name} (${response.status})`)
  }
  ctx.reader = response.body.getReader()
}

// Pump one chunk of the open entry, closing it at end of stream.
const pumpChunk = async ctx => {
  const { done, value } = await ctx.reader.read()
  if (done) {
    ctx.zip.endEntry()
    ctx.reader = null
    ctx.index += 1
    return
  }
  ctx.zip.pushData(value)
  ctx.loaded += value.length
  if (!ctx.onProgress) return
  ctx.onProgress({
    loaded: ctx.loaded,
    fileIndex: ctx.index,
    fileCount: ctx.assets.length,
    currentFile: ctx.assets[ctx.index].name,
  })
}

const stepSource = ctx => (ctx.reader === null ? openNextEntry(ctx) : pumpChunk(ctx))

// Pull-driven zip producer: the browser's own download consumption sets the
// pace (backpressure), each S3 file is fetched just in time.
export const createZipChunkSource = (assets, { onProgress } = {}) => {
  const pending = []
  const ctx = {
    assets,
    onProgress,
    zip: new ZipStreamWriter(chunk => pending.push(chunk)),
    entryName: uniqueEntryName(new Set()),
    index: 0,
    reader: null,
    loaded: 0,
    closed: false,
    settle: {},
  }
  const finished = new Promise((resolve, reject) => {
    ctx.settle = { resolve, reject }
  })

  return {
    finished,
    async pull(controller) {
      try {
        // eslint-disable-next-line no-await-in-loop
        while (!pending.length && !ctx.closed) await stepSource(ctx)
        while (pending.length) controller.enqueue(pending.shift())
        if (ctx.closed) controller.close()
      } catch (err) {
        ctx.settle.reject(err)
        controller.error(err)
      }
    },
    async cancel() {
      if (ctx.reader) await ctx.reader.cancel().catch(() => {})
      ctx.closed = true
      ctx.settle.reject(new ClientZipCancelled())
    },
  }
}

const SW_PATH = '/kp-zip-sw.js'
const SW_SCOPE = '/__kp-zip/'

const zipWorkerReady = async () => {
  const registration = await navigator.serviceWorker.register(SW_PATH, { scope: SW_SCOPE })
  const worker = registration.active || registration.waiting || registration.installing
  if (!worker) throw new Error('zip download worker unavailable')
  if (worker.state === 'activated') return worker
  await new Promise(resolve => {
    const onState = () => {
      if (worker.state === 'activated') {
        worker.removeEventListener('statechange', onState)
        resolve()
      }
    }
    worker.addEventListener('statechange', onState)
  })
  return worker
}

// The stream is TRANSFERRED to the worker; the ack port confirms it holds
// the entry before the download navigation asks for it.
const announceStream = (worker, { id, name }, stream) => new Promise((resolve, reject) => {
  const channel = new MessageChannel()
  const timer = setTimeout(() => reject(new Error('zip download worker did not answer')), 10000)
  channel.port1.onmessage = () => {
    clearTimeout(timer)
    resolve()
  }
  worker.postMessage({
    type: 'kp-zip-stream', id, name, stream, port: channel.port2,
  }, [stream, channel.port2])
})

const saveViaServiceWorker = async ({
  assets, zipName, onProgress, onStarted,
}) => {
  const worker = await zipWorkerReady()
  const source = createZipChunkSource(assets, { onProgress })
  const stream = new ReadableStream({
    pull: controller => source.pull(controller),
    cancel: () => source.cancel(),
  })
  const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`
  await announceStream(worker, { id, name: `${zipName}.zip` }, stream)
  const anchor = document.createElement('a')
  anchor.href = `${SW_SCOPE}${id}`
  anchor.style.display = 'none'
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  // The browser download is on its shelf now.
  if (onStarted) onStarted()
  return source.finished
}

// ---------------------------------------------------------------------------

/**
 * Build the zip in-browser. MUST be called directly from a click handler —
 * the picker strategy needs the user gesture, so the dialog opens BEFORE any
 * network. The service-worker strategy delivers into the Downloads folder.
 * onProgress fires with {loaded, fileIndex, fileCount, currentFile}.
 */
// Everything the archive should contain: the stored files, plus a freshly
// generated order form when the request asked for one. The order form is
// generated first so a failure surfaces before any bytes are written.
const collectAssets = async ({
  collectionId, deliverable, selectedAssets, retailerId, onPreparing,
}) => {
  const orderForm = wantsOrderForm(deliverable, selectedAssets)
    ? await (async () => {
      if (onPreparing) onPreparing()
      return prepareOrderForm({ collectionId, retailerId })
    })()
    : null
  const stored = await resolveAssets({ collectionId, deliverable, selectedAssets })
  return orderForm ? [orderForm, ...stored] : stored
}

export async function downloadClientZip({
  collectionId, deliverable, selectedAssets, retailerId, zipName,
  onProgress, onStarted, onPreparing,
}) {
  const gather = () => collectAssets({
    collectionId, deliverable, selectedAssets, retailerId, onPreparing,
  })
  if (supportsPicker()) {
    let handle
    try {
      handle = await window.showSaveFilePicker({
        suggestedName: `${zipName}.zip`,
        types: [{ description: 'ZIP archive', accept: { 'application/zip': ['.zip'] } }],
      })
    } catch (err) {
      if (err && err.name === 'AbortError') throw new ClientZipCancelled()
      throw err
    }
    const assets = await gather()
    // The spot is chosen and the writing begins — the download has started.
    if (onStarted) onStarted()
    return saveToHandle({ handle, assets, onProgress })
  }
  const assets = await gather()
  return saveViaServiceWorker({
    assets, zipName, onProgress, onStarted,
  })
}
